import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapStore, type EditWaypoint } from '@shared/hooks/useMapStore';
import { useSaveRoute } from './useSaveRoute';
import type { Waypoint } from '../types';

interface UseRouteEditReturn {
  isEditing: boolean;
  isDirty: boolean;
  dirtyWaypoints: Waypoint[];
  dirtyName: string;
  routeId: string | null;
  isSaving: boolean;
  startEdit: (routeId: string, name: string, waypoints: Waypoint[]) => void;
  saveEdit: (makeActive?: boolean) => Promise<void>;
  revertEdit: () => void;
  cancelEdit: () => void;
  setName: (name: string) => void;
  addVertex: (afterWaypointIndex: number, lat: number, lon: number) => void;
  moveVertex: (waypointIndex: number, lat: number, lon: number) => void;
  deleteVertex: (waypointIndex: number) => void;
}

function waypointsToEditWaypoints(waypoints: Waypoint[]): EditWaypoint[] {
  return waypoints.map((wp) => ({
    index: wp.index,
    lat: wp.lat,
    lon: wp.lon,
    name: wp.name,
  }));
}

export function useRouteEdit(): UseRouteEditReturn {
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [originalName, setOriginalName] = useState('');
  const [dirtyName, setDirtyName] = useState('');
  const [originalWaypoints, setOriginalWaypoints] = useState<Waypoint[]>([]);
  const [dirtyWaypoints, setDirtyWaypoints] = useState<Waypoint[]>([]);

  const setRouteEditMode = useMapStore((s) => s.setRouteEditMode);
  const setEditWaypoints = useMapStore((s) => s.setEditWaypoints);
  const saveRoute = useSaveRoute();

  const pushingToStore = useRef(false);

  // Sync dirtyWaypoints -> map store
  useEffect(() => {
    if (editingRouteId) {
      pushingToStore.current = true;
      setEditWaypoints(waypointsToEditWaypoints(dirtyWaypoints));
      queueMicrotask(() => {
        pushingToStore.current = false;
      });
    }
  }, [editingRouteId, dirtyWaypoints, setEditWaypoints]);

  // Sync map store -> dirtyWaypoints (after OL Modify drag)
  // Uses Zustand subscribe to avoid setState-in-effect lint violation.
  useEffect(() => {
    if (!editingRouteId) return;

    const unsub = useMapStore.subscribe((state, prevState) => {
      if (state.editWaypoints === prevState.editWaypoints) return;
      if (pushingToStore.current || !state.editWaypoints) return;

      const updated = state.editWaypoints;
      setDirtyWaypoints((prev) =>
        prev.map((wp, i) => {
          const u = updated[i];
          return u ? { ...wp, lat: u.lat, lon: u.lon } : wp;
        }),
      );
    });
    return unsub;
  }, [editingRouteId]);

  // Consume pending edit actions via Zustand subscribe
  useEffect(() => {
    if (!editingRouteId) return;

    const unsub = useMapStore.subscribe((state, prevState) => {
      if (state.pendingEditAction === prevState.pendingEditAction) return;
      const action = state.pendingEditAction;
      if (!action) return;

      if (action.type === 'addVertex') {
        const { afterWaypointIndex, lat, lon } = action;
        setDirtyWaypoints((prev) => {
          const next = [...prev];
          next.splice(afterWaypointIndex + 1, 0, {
            index: afterWaypointIndex + 1,
            lat,
            lon,
          });
          return next.map((wp, i) => ({ ...wp, index: i }));
        });
      } else if (action.type === 'deleteVertex') {
        const { waypointIndex } = action;
        setDirtyWaypoints((prev) =>
          prev.filter((_, i) => i !== waypointIndex).map((wp, i) => ({ ...wp, index: i })),
        );
      }

      useMapStore.getState().setPendingEditAction(null);
    });
    return unsub;
  }, [editingRouteId]);

  const isDirty =
    dirtyName !== originalName ||
    JSON.stringify(dirtyWaypoints) !== JSON.stringify(originalWaypoints);

  const startEdit = useCallback(
    (routeId: string, name: string, waypoints: Waypoint[]) => {
      setEditingRouteId(routeId);
      setOriginalName(name);
      setDirtyName(name);
      setOriginalWaypoints(waypoints);
      setDirtyWaypoints(waypoints);
      setEditWaypoints(waypointsToEditWaypoints(waypoints));
      setRouteEditMode(true);
    },
    [setRouteEditMode, setEditWaypoints],
  );

  const cancelEdit = useCallback(() => {
    setDirtyWaypoints(originalWaypoints);
    setDirtyName(originalName);
    setEditingRouteId(null);
    setEditWaypoints(null);
    setRouteEditMode(false);
  }, [originalWaypoints, originalName, setRouteEditMode, setEditWaypoints]);

  const revertEdit = useCallback(() => {
    setDirtyWaypoints(originalWaypoints);
    setDirtyName(originalName);
  }, [originalWaypoints, originalName]);

  const saveEdit = useCallback(
    async (makeActive = false) => {
      if (!editingRouteId) return;
      await saveRoute.mutateAsync({
        routeId: editingRouteId,
        name: dirtyName,
        waypoints: dirtyWaypoints,
        isCurrent: makeActive || undefined,
      });
      setOriginalName(dirtyName);
      setOriginalWaypoints(dirtyWaypoints);
    },
    [editingRouteId, dirtyName, dirtyWaypoints, saveRoute],
  );

  const addVertex = useCallback((afterWaypointIndex: number, lat: number, lon: number) => {
    setDirtyWaypoints((prev) => {
      const next = [...prev];
      next.splice(afterWaypointIndex + 1, 0, {
        index: afterWaypointIndex + 1,
        lat,
        lon,
      });
      return next.map((wp, i) => ({ ...wp, index: i }));
    });
  }, []);

  const moveVertex = useCallback((waypointIndex: number, lat: number, lon: number) => {
    setDirtyWaypoints((prev) =>
      prev.map((wp, i) => (i === waypointIndex ? { ...wp, lat, lon } : wp)),
    );
  }, []);

  const deleteVertex = useCallback((waypointIndex: number) => {
    setDirtyWaypoints((prev) =>
      prev.filter((_, i) => i !== waypointIndex).map((wp, i) => ({ ...wp, index: i })),
    );
  }, []);

  return {
    isEditing: editingRouteId !== null,
    isDirty,
    dirtyWaypoints,
    dirtyName,
    routeId: editingRouteId,
    isSaving: saveRoute.isPending,
    startEdit,
    saveEdit,
    revertEdit,
    cancelEdit,
    setName: setDirtyName,
    addVertex,
    moveVertex,
    deleteVertex,
  };
}
